import { PUBLIC_CLIENT_URL } from '../../shared/network/invites.js';

/** Hosts whose pages keep the build on disk (`service-worker.js`). Loopback and tunnel addresses change port or name each session, so a
 * cache there would be stranded; development builds have no worker. */
const CACHED_HOSTS = [new URL(PUBLIC_CLIENT_URL).hostname];
/** After loading settles, the rest of the build (music aside) is fetched in the background so a later launch needs no network for assets. */
const FILL_DELAY_MS = 60_000;

export function registerInstallCache() {
  if (!import.meta.env.PROD || !('serviceWorker' in navigator) || !CACHED_HOSTS.includes(location.hostname)) return;
  const register = () => {
    navigator.serviceWorker.register('/sw.js').catch(() => undefined);
    void navigator.serviceWorker.ready.then(registration => {
      // Files this page fetched before the worker took control are mostly in the HTTP cache, so keeping them costs no download.
      const urls = performance.getEntriesByType('resource').map(entry => entry.name).filter(name => new URL(name).origin === location.origin);
      registration.active?.postMessage({ type: 'keep', urls });
      const connection = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection;
      if (!connection?.saveData) setTimeout(() => registration.active?.postMessage({ type: 'fill' }), FILL_DELAY_MS);
    });
  };
  // The game module awaits its setup at top level, so loading has often finished by the time this runs.
  if (document.readyState === 'complete') register(); else addEventListener('load', register, { once: true });
}

/** Chrome and Edge's install offer, held back from their own prompt so the main menu can make it. */
interface InstallPrompt extends Event { prompt(): Promise<unknown> }
let offer: InstallPrompt | null = null;
let shown: ((available: boolean) => void) | null = null;
// Listening as soon as this module loads: the browser may make its offer before the menu is built.
addEventListener('beforeinstallprompt', event => { event.preventDefault(); offer = event as InstallPrompt; shown?.(true); });
addEventListener('appinstalled', () => { offer = null; shown?.(false); });

/** Shows `button` while the game can be installed (not yet installed, on a browser that offers it) and installs on click. */
export function offerInstall(button: HTMLButtonElement) {
  shown = available => { button.hidden = !available; };
  shown(!!offer);
  button.addEventListener('click', async () => {
    const pending = offer;
    if (!pending) return;
    // An offer is good for one prompt; a dismissed one comes back when the browser makes it again.
    offer = null; shown?.(false);
    await pending.prompt();
  });
}

/** Shows `button` only in an installed app window, which has no browser chrome to close it with, and closes the window on click. With
 * `confirm` the first click arms the button (it shows `armed`) and a second within a few seconds closes. */
export function offerClose(button: HTMLButtonElement, confirm?: { armed: string }) {
  if (!matchMedia('(display-mode: standalone), (display-mode: window-controls-overlay), (display-mode: minimal-ui)').matches) return;
  button.hidden = false;
  const label = button.innerHTML;
  let timer: ReturnType<typeof setTimeout> | undefined;
  button.addEventListener('click', () => {
    if (confirm && timer === undefined) {
      button.textContent = confirm.armed;
      timer = setTimeout(() => { timer = undefined; button.innerHTML = label; }, 4000);
      return;
    }
    window.close();
  });
}
